/**
 * ComplianceDashboardPage
 * Route: /admin/compliance
 *
 * DPO compliance dashboard with 5 scorecards, action center, and report archive.
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ShieldCheck,
  FileText,
  Users,
  Clock,
  AlertTriangle,
  CheckCircle,
  Loader2,
  XCircle,
  Bell,
  Lock,
  FileWarning,
  RefreshCw,
  Download,
  ChevronRight,
  Calendar,
  TrendingUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  getComplianceScorecard,
  getDPOActionCenter,
  generateMonthlyReport,
  listComplianceReports,
} from "@/services/dpdpApi";

function TrafficLight({ score, label, detail, urgent }) {
  const color =
    score >= 90 ? "text-green-500" : score >= 70 ? "text-amber-500" : "text-red-500";
  const bg =
    score >= 90 ? "bg-green-500" : score >= 70 ? "bg-amber-500" : "bg-red-500";
  const ring =
    score >= 90
      ? "ring-green-500/50"
      : score >= 70
      ? "ring-amber-500/50"
      : "ring-red-500/50";

  return (
    <div className={`flex items-center gap-3 p-4 rounded-xl bg-white/5 border border-white/10 ${urgent ? "ring-2 " + ring : ""}`}>
      <div className={`w-4 h-4 rounded-full ${bg} flex-shrink-0`} />
      <div className="flex-1 min-w-0">
        <p className={`text-xl font-bold ${color}`}>{score.toFixed(1)}%</p>
        <p className="text-sm text-white/70">{label}</p>
        {detail && <p className="text-xs text-white/40 mt-0.5">{detail}</p>}
      </div>
    </div>
  );
}

function UrgencyBadge({ urgency }) {
  const styles = {
    critical: "bg-red-600 text-white",
    high: "bg-orange-500 text-white",
    medium: "bg-yellow-500 text-black",
    low: "bg-slate-600 text-white",
  };
  return (
    <Badge className={styles[urgency] || styles.low}>
      {urgency}
    </Badge>
  );
}

function ActionCard({ item }) {
  const navigate = useNavigate();
  return (
    <div className="flex items-center gap-4 p-4 rounded-lg bg-white/5 border border-white/5 hover:border-white/20 transition-all">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <UrgencyBadge urgency={item.urgency} />
          <span className="text-sm font-medium text-white truncate">{item.title}</span>
        </div>
        <p className="text-xs text-white/40 truncate">{item.description}</p>
        {item.days_remaining !== null && item.days_remaining !== undefined && (
          <p className={`text-xs mt-1 ${item.days_remaining < 0 ? "text-red-400" : item.days_remaining <= 2 ? "text-orange-400" : "text-white/40"}`}>
            {item.days_remaining < 0
              ? `${Math.abs(item.days_remaining)}d overdue`
              : `${item.days_remaining}d remaining`}
          </p>
        )}
      </div>
      <Button
        size="sm"
        className="bg-indigo-600 hover:bg-indigo-700 flex-shrink-0"
        onClick={() => item.action_url && navigate(item.action_url)}
      >
        {item.action_label} <ChevronRight className="h-3 w-3 ml-1" />
      </Button>
    </div>
  );
}

export default function ComplianceDashboardPage() {
  const navigate = useNavigate();
  const [scorecard, setScorecard] = useState(null);
  const [actions, setActions] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState("scorecard");

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [sc, ac, rep] = await Promise.all([
        getComplianceScorecard().catch(() => null),
        getDPOActionCenter().catch(() => null),
        listComplianceReports({ limit: 12 }).catch(() => ({ items: [] })),
      ]);
      setScorecard(sc);
      setActions(ac);
      setReports(rep.items || []);
    } catch (err) {
      toast.error("Failed to load compliance data");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = async () => {
    const now = new Date();
    const year = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
    const month = now.getMonth() === 0 ? 12 : now.getMonth();
    setGenerating(true);
    try {
      const report = await generateMonthlyReport(year, month);
      toast.success(`Report for ${year}-${String(month).padStart(2, "0")} generated`);
      await loadData();
    } catch (err) {
      toast.error(err.message || "Failed to generate report");
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950/20 to-slate-900 flex items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-indigo-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950/10 to-slate-900">
      {/* Header */}
      <header className="border-b border-white/10 bg-black/20 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-4">
          <div className="flex-1">
            <h1 className="font-display text-lg font-bold text-white flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-indigo-400" />
              DPO Compliance Dashboard
            </h1>
            <p className="text-xs text-white/50">DPDP Act 2023 — Compliance Scorecard & Action Center</p>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={loadData}
            className="border-indigo-500/50 text-indigo-300 hover:bg-indigo-500/10"
          >
            <RefreshCw className="h-3 w-3 mr-2" />
            Refresh
          </Button>
          <Button
            size="sm"
            className="bg-indigo-600 hover:bg-indigo-700"
            onClick={handleGenerateReport}
            disabled={generating}
          >
            {generating ? <Loader2 className="h-3 w-3 mr-2 animate-spin" /> : <Calendar className="h-3 w-3 mr-2" />}
            Generate Report
          </Button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Critical Alert Banner */}
        {actions?.critical_count > 0 && (
          <div className="mb-6 rounded-lg bg-red-950/40 border border-red-500/40 p-4 flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-red-400 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-red-300">
                {actions.critical_count} Critical Action Required
              </p>
              <p className="text-xs text-red-200/60">
                Review and address critical compliance items immediately
              </p>
            </div>
            <Badge className="bg-red-600 text-white">{actions.critical_count} Critical</Badge>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="scorecard">Scorecard</TabsTrigger>
            <TabsTrigger value="actions">Action Center</TabsTrigger>
            <TabsTrigger value="reports">Reports</TabsTrigger>
            <TabsTrigger value="audit">Audit Export</TabsTrigger>
          </TabsList>

          {/* Scorecard Tab */}
          <TabsContent value="scorecard">
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {scorecard ? (
                <>
                  <TrafficLight
                    score={scorecard.consent_health.score}
                    label="Consent Health"
                    detail={`${scorecard.consent_health.principals_with_consent} / ${scorecard.consent_health.total_active_principals} with consent`}
                    urgent={scorecard.consent_health.traffic_light === "red"}
                  />
                  <TrafficLight
                    score={scorecard.rights_sla.score}
                    label="Rights SLA Compliance"
                    detail={`${scorecard.rights_sla.overdue_count} overdue requests`}
                    urgent={scorecard.rights_sla.traffic_light === "red"}
                  />
                  <TrafficLight
                    score={scorecard.retention_compliance.score}
                    label="Retention Compliance"
                    detail={`${scorecard.retention_compliance.no_policy_count} without policy`}
                    urgent={scorecard.retention_compliance.traffic_light === "red"}
                  />
                  <TrafficLight
                    score={scorecard.breach_response.dpb_notified_pct}
                    label="Breach Response"
                    detail={`${scorecard.breach_response.total_breaches} breaches (12m) — ${scorecard.breach_response.open_breaches} open`}
                    urgent={scorecard.breach_response.critical_alert}
                  />
                  <TrafficLight
                    score={scorecard.notice_coverage.score}
                    label="Notice Coverage"
                    detail={`${scorecard.notice_coverage.gated_points} / ${scorecard.notice_coverage.total_points} gated`}
                    urgent={scorecard.notice_coverage.traffic_light === "red"}
                  />
                </>
              ) : (
                <div className="col-span-5 text-center py-8 text-white/40">
                  Scorecard data unavailable
                </div>
              )}
            </div>

            {/* Detailed breakdown */}
            {scorecard && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-indigo-400" />
                      Score Summary
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {[
                      { label: "Consent Health", score: scorecard.consent_health.score, color: "bg-indigo-500" },
                      { label: "Rights SLA", score: scorecard.rights_sla.score, color: "bg-blue-500" },
                      { label: "Retention", score: scorecard.retention_compliance.score, color: "bg-purple-500" },
                      { label: "Breach Response", score: scorecard.breach_response.dpb_notified_pct, color: "bg-orange-500" },
                      { label: "Notice Coverage", score: scorecard.notice_coverage.score, color: "bg-green-500" },
                    ].map(({ label, score, color }) => (
                      <div key={label}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-white/70">{label}</span>
                          <span className="text-white font-medium">{score.toFixed(1)}%</span>
                        </div>
                        <Progress value={score} className={`h-2 [&>div]:${color}`} />
                      </div>
                    ))}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-400" />
                      Action Summary
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div className="bg-white/5 rounded-lg p-3">
                        <div className="text-2xl font-bold text-red-400">{actions?.critical_count || 0}</div>
                        <div className="text-xs text-white/40">Critical</div>
                      </div>
                      <div className="bg-white/5 rounded-lg p-3">
                        <div className="text-2xl font-bold text-orange-400">{actions?.high_count || 0}</div>
                        <div className="text-xs text-white/40">High</div>
                      </div>
                      <div className="bg-white/5 rounded-lg p-3">
                        <div className="text-2xl font-bold text-white">{actions?.total_items || 0}</div>
                        <div className="text-xs text-white/40">Total</div>
                      </div>
                    </div>
                    {actions?.total_items > 0 && (
                      <Button
                        variant="outline"
                        className="w-full mt-4 border-indigo-500/50 text-indigo-300 hover:bg-indigo-500/10"
                        onClick={() => setActiveTab("actions")}
                      >
                        View All Actions <ChevronRight className="h-3 w-3 ml-1" />
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* Action Center Tab */}
          <TabsContent value="actions">
            <Card>
              <CardHeader className="flex-row items-center justify-between space-y-0 pb-2">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Bell className="h-4 w-4 text-amber-400" />
                    DPO Action Center
                  </CardTitle>
                  <CardDescription>
                    {actions?.total_items || 0} items requiring attention
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  {actions?.critical_count > 0 && (
                    <Badge className="bg-red-600">{actions.critical_count} Critical</Badge>
                  )}
                  {actions?.high_count > 0 && (
                    <Badge className="bg-orange-500">{actions.high_count} High</Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                {!actions || actions.items.length === 0 ? (
                  <div className="text-center py-12">
                    <CheckCircle className="h-12 w-12 text-green-400 mx-auto mb-4" />
                    <p className="text-white/50 text-sm">No actions required — all systems compliant</p>
                  </div>
                ) : (
                  actions.items.map((item) => <ActionCard key={item.action_id} item={item} />)
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports">
            <Card>
              <CardHeader className="flex-row items-center justify-between space-y-0 pb-2">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <FileText className="h-4 w-4 text-indigo-400" />
                    Compliance Reports Archive
                  </CardTitle>
                  <CardDescription>Monthly compliance reports, retained for 3 years</CardDescription>
                </div>
                <Button
                  size="sm"
                  className="bg-indigo-600 hover:bg-indigo-700"
                  onClick={handleGenerateReport}
                  disabled={generating}
                >
                  <Calendar className="h-3 w-3 mr-2" />
                  Generate This Month
                </Button>
              </CardHeader>
              <CardContent>
                {reports.length === 0 ? (
                  <div className="text-center py-12">
                    <FileWarning className="h-12 w-12 text-white/20 mx-auto mb-4" />
                    <p className="text-white/40 text-sm">No reports generated yet</p>
                    <Button
                      variant="outline"
                      className="mt-4"
                      onClick={handleGenerateReport}
                      disabled={generating}
                    >
                      Generate First Report
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {reports.map((report) => (
                      <div
                        key={report.report_id}
                        className="flex items-center gap-4 p-4 rounded-lg bg-white/5 border border-white/5 hover:border-white/20 transition-all"
                      >
                        <FileText className="h-8 w-8 text-indigo-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-white">{report.month}</p>
                          <p className="text-xs text-white/40 font-mono">{report.report_id}</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-xs text-white/50">
                            {new Date(report.generated_at).toLocaleDateString()}
                          </p>
                          {report.status === "signed" ? (
                            <Badge className="bg-green-600 text-white text-xs mt-1">Signed</Badge>
                          ) : (
                            <Badge variant="outline" className="border-amber-500/50 text-amber-400 text-xs mt-1">
                              Unsigned
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Audit Export Tab */}
          <TabsContent value="audit">
            <AuditExportSection />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function AuditExportSection() {
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().split("T")[0];
  });
  const [toDate, setToDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [actionTypes, setActionTypes] = useState("");
  const [resourceTypes, setResourceTypes] = useState("");
  const [exporting, setExporting] = useState(false);
  const [result, setResult] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);

  const handleExport = async () => {
    setExporting(true);
    setResult(null);
    try {
      const data = await (await import("@/services/dpdpApi")).exportAuditLog({
        from_date: fromDate,
        to_date: toDate,
        action_types: actionTypes,
        resource_types: resourceTypes,
      });
      setResult(data);
      toast.success(`Exported ${data.total_records} audit records`);
    } catch (err) {
      toast.error(err.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const handleVerify = async () => {
    setVerifying(true);
    setVerifyResult(null);
    try {
      const data = await (await import("@/services/dpdpApi")).verifyAuditChain({
        from_sequence: 0,
        to_sequence: 999999999,
      });
      setVerifyResult(data);
      if (data.valid) {
        toast.success("Audit chain verified — no tampering detected");
      } else {
        toast.error(`Tampering detected at sequence ${data.first_tampered_sequence}`);
      }
    } catch (err) {
      toast.error(err.message || "Verification failed");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Audit Chain Verification</CardTitle>
          <CardDescription>Verify the tamper-evident hash chain integrity</CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            className="w-full bg-indigo-600 hover:bg-indigo-700"
            onClick={handleVerify}
            disabled={verifying}
          >
            {verifying ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
            {verifying ? "Verifying..." : "Run Chain Verification"}
          </Button>
          {verifyResult && (
            <div className={`mt-4 p-4 rounded-lg ${verifyResult.valid ? "bg-green-950/20 border border-green-500/30" : "bg-red-950/20 border border-red-500/30"}`}>
              <div className="flex items-center gap-2 mb-2">
                {verifyResult.valid ? (
                  <CheckCircle className="h-5 w-5 text-green-400" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-400" />
                )}
                <p className={`font-semibold ${verifyResult.valid ? "text-green-400" : "text-red-400"}`}>
                  {verifyResult.valid ? "Chain Valid — No Tampering" : "TAMPERING DETECTED"}
                </p>
              </div>
              <p className="text-sm text-white/60">
                Verified {verifyResult.total_verified} entries in {verifyResult.verification_time_ms}ms
              </p>
              {!verifyResult.valid && (
                <p className="text-sm text-red-400 mt-1">
                  First tampered: sequence {verifyResult.first_tampered_sequence} (ID: {verifyResult.first_tampered_audit_id})
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Download className="h-4 w-4 text-indigo-400" />
            Export Audit Log for DPB
          </CardTitle>
          <CardDescription>
            Generate a signed JSON export of audit entries for submission to the Data Protection Board
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-white/70">From Date</label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="mt-1 w-full h-10 px-3 rounded-md border border-white/20 bg-white/10 text-white"
              />
            </div>
            <div>
              <label className="text-sm text-white/70">To Date</label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="mt-1 w-full h-10 px-3 rounded-md border border-white/20 bg-white/10 text-white"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-white/70">Action Types (comma-separated)</label>
              <input
                type="text"
                value={actionTypes}
                onChange={(e) => setActionTypes(e.target.value)}
                placeholder="e.g., CONSENT_GRANTED, DPR_REQUEST_RECEIVED"
                className="mt-1 w-full h-10 px-3 rounded-md border border-white/20 bg-white/10 text-white text-sm"
              />
            </div>
            <div>
              <label className="text-sm text-white/70">Resource Types (comma-separated)</label>
              <input
                type="text"
                value={resourceTypes}
                onChange={(e) => setResourceTypes(e.target.value)}
                placeholder="e.g., consent_record, dpr_request"
                className="mt-1 w-full h-10 px-3 rounded-md border border-white/20 bg-white/10 text-white text-sm"
              />
            </div>
          </div>
          <Button
            className="w-full bg-green-600 hover:bg-green-700"
            onClick={handleExport}
            disabled={exporting}
          >
            {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
            {exporting ? "Exporting..." : "Export & Sign for DPB"}
          </Button>

          {result && (
            <div className="mt-4 p-4 rounded-lg bg-white/5 border border-green-500/30">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="h-5 w-5 text-green-400" />
                <p className="font-semibold text-green-400">Export Complete</p>
              </div>
              <div className="space-y-1 text-sm text-white/60">
                <p>Records: {result.total_records}</p>
                <p>Exported: {new Date(result.exported_at).toLocaleString()}</p>
                <p className="font-mono text-xs text-white/40">
                  Signature: {result.export_signature?.substring(0, 16)}...
                </p>
                <p className="font-mono text-xs text-white/40">
                  Key Fingerprint: {result.public_key_fingerprint}
                </p>
                <p className={`text-xs ${result.chain_verification?.valid ? "text-green-400" : "text-red-400"}`}>
                  Chain Valid: {result.chain_verification?.valid ? "Yes" : "No — tampering detected"}
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
